import { AdditionalDataResponse } from '@/entities/ihamkor/types';
import {
  Box,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import classes from './subject-additional-info.module.sass';
import { useTranslation } from 'react-i18next';
import { UIDefinitionList } from '@/shared/ui/ui-definition-list';
import { triada } from '@/app/helpers';
import { UITable } from '@/shared/ui/ui-table';
import { useGetAdditionalDataQuery } from '@/app/api';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';

interface Props {
  unique: number;
  tin: string;
  isBriefDataLoading?: boolean;
  isBriefDataError?: boolean;
}

export const CompanyAdditionalData: FC<Props> = ({ unique, tin, isBriefDataLoading }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetAdditionalDataQuery(
    {
      unique,
      tin,
    },
    { skip: !unique || !tin }
  );

  return (
    <ComponentWithLoader isLoading={isBriefDataLoading || isLoading}>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box sx={{ pt: 3, px: 3 }} className={classes['main']}>
          <Typography variant='h4' className={classes['title']}>
            {t('AddInfoTab.title_1')}
          </Typography>

          <Stack
            direction='row'
            mt={4}
            spacing={2}
            className={classes['row']}
            useFlexGap
            flexWrap='wrap'
          >
            <UIDefinitionList
              sx={{ flex: 1 }}
              term={t('AddInfoTab.gross_margin')}
              description={String(data?.data?.grossMargin || '')}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <UIDefinitionList
              sx={{ flex: 1 }}
              term={t('AddInfoTab.return_on_sales')}
              description={String(data?.data?.salesMargin || '')}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <UIDefinitionList
              sx={{ flex: 1 }}
              term={t('AddInfoTab.cost_return')}
              description={String(data?.data.costsMargin || '')}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
          </Stack>

          <Typography variant='h4' className={classes['title']} mt={4}>
            {t('AddInfoTab.employees_count')}
          </Typography>

          <Stack
            direction='row'
            mt={4}
            spacing={2}
            useFlexGap
            flexWrap='wrap'
            className={classes['row']}
          >
            <UIDefinitionList
              sx={{ flex: 1 }}
              term={t('AddInfoTab.average_annual_number')}
              description={triada(String(data?.data.averageAnnualNumberOfEmployees || ''))}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <UIDefinitionList
              sx={{ flex: 1 }}
              term={t('AddInfoTab.employee_count')}
              description={triada(String(data?.data.requestPeriodNumberOfEmployees || ''))}
              descriptionSx={{
                fontSize: '2.2rem',
              }}
            />
            <Box sx={{ flex: 1 }}></Box>
          </Stack>

          <Divider sx={{ my: 4 }} />

          <Stack justifyContent='space-between' spacing={1}>
            <Typography variant='h4' className={classes['title']}>
              {t('AddInfoTab.title_2')}
            </Typography>
            <Typography variant='caption' fontSize={14}>
              {data?.data.incomeStatement.infoRu}
            </Typography>
          </Stack>

          <TableContainer sx={{ mt: 4 }}>
            {!!data?.data.incomeStatement.rows?.length && (
              <Typography
                variant='caption'
                sx={{
                  float: 'right',
                  fontSize: '1.4rem',
                }}
              >
                {t('AddInfoTab.table_1_description')}
              </Typography>
            )}
            <Table
              sx={{
                tableLayout: 'fixed',
                border: `1px solid #F1F1F1`,
                borderCollapse: 'collapse',
                '& .MuiTableHead-root': {
                  backgroundColor: 'rgba(13, 29, 88, 0.04)',
                  '& .MuiTableCell-root': {
                    color: 'text.secondary',
                  },
                },
                '& th, td': {
                  border: `1px solid`,
                  borderColor: 'divider',
                },
              }}
              className={classes['table']}
            >
              {!!data?.data.incomeStatement.rows?.length && (
                <TableHead>
                  <TableRow>
                    <TableCell
                      rowSpan={2}
                      sx={{
                        width: '30%',
                      }}
                    >
                      {t('AddInfoTab.table_label_2')}
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      align='center'
                      sx={{
                        whiteSpace: 'nowrap',
                        width: '10%',
                      }}
                    >
                      {t('AddInfoTab.table_label_1')}
                    </TableCell>
                    <TableCell colSpan={2} align='center'>
                      {t('AddInfoTab.table_label_3')}
                    </TableCell>
                    <TableCell colSpan={2} align='center'>
                      {t('AddInfoTab.table_label_4')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='center'>{t('AddInfoTab.table_label_3_1')}</TableCell>
                    <TableCell align='center'>{t('AddInfoTab.table_label_3_2')}</TableCell>
                    <TableCell align='center'>{t('AddInfoTab.table_label_3_1')}</TableCell>
                    <TableCell align='center'>{t('AddInfoTab.table_label_3_2')}</TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {data?.data.incomeStatement.rows.length ? (
                  data?.data.incomeStatement.rows.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{}}>{item?.name}</TableCell>
                      <TableCell
                        sx={{
                          // color: colors.fontGray,
                          textAlign: 'center',
                        }}
                      >
                        {item?.code}
                      </TableCell>
                      <TableCell align='right'>
                        {triada(item?.incomeLastYear, {
                          decimal: 2,
                        })}
                      </TableCell>
                      <TableCell align='right'>
                        {triada(item?.costsLastYear, {
                          decimal: 2,
                        })}
                      </TableCell>
                      <TableCell align='right'>
                        {triada(item?.incomeThisYear, {
                          decimal: 2,
                        })}
                      </TableCell>
                      <TableCell align='right'>
                        {triada(item?.costsThisYear, {
                          decimal: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align='center'>
                      <Typography variant='caption'>
                        {t('AddInfoTab.no_info_from_subject')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <UITable
            sx={{
              mt: 4,
              '& .MuiTableHead-root': {
                backgroundColor: 'rgba(13, 29, 88, 0.04)',
                '& .MuiTableCell-root': {
                  color: 'text.secondary',
                },
              },
            }}
            data={
              data?.data.budgetPaymentsCertificate ||
              ([{}] as AdditionalDataResponse['budgetPaymentsCertificate'])
            }
            columns={[
              {
                label: t('AddInfoTab.table_label_2'),
                key: 'name',
                sx: {
                  width: '30%',
                },
              },
              {
                label: t('AddInfoTab.table_label_1'),
                key: 'code',
                sx: {
                  width: '10%',
                },
                align: 'center',
                headerAlign: 'center',
              },
              {
                label: t('AddInfoTab.table_label_18'),
                render: ({ dueForReportingPeriod }) =>
                  triada(dueForReportingPeriod, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_19'),
                render: ({ factForReportingPeriod }) =>
                  triada(factForReportingPeriod, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
            ]}
            EmptyPlaceholder={
              <Typography variant='caption'>{t('AddInfoTab.no_info_from_subject')}</Typography>
            }
          />

          <Stack spacing={1} mt={4}>
            <Typography variant='h4' className={classes['title']}>
              {t('AddInfoTab.title_3')}
            </Typography>
            <Typography variant='caption' fontSize={14}>
              {data?.data.droppedTaxesInfo}
            </Typography>
          </Stack>

          <UITable
            sx={{
              mt: 4,
              '& .MuiTableHead-root': {
                backgroundColor: 'rgba(13, 29, 88, 0.04)',
                '& .MuiTableCell-root': {
                  color: 'text.secondary',
                },
              },
            }}
            data={data?.data.droppedTaxes || ([{}] as AdditionalDataResponse['droppedTaxes'])}
            columns={[
              {
                label: t('AddInfoTab.table_label_7'),
                key: 'taxType',
                sx: {
                  width: 'calc(40%)',
                },
              },
              {
                label: t('AddInfoTab.table_label_8'),
                render: ({ accruedAmount }) =>
                  triada(accruedAmount, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_10'),
                render: ({ droppedAmount }) =>
                  triada(droppedAmount, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_11'),
                render: ({ penalty }) =>
                  triada(penalty, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_12'),
                render: ({ prepayment }) =>
                  triada(prepayment, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
            ]}
            EmptyPlaceholder={
              <Typography variant='caption'>'{t('AddInfoTab.no_info_from_subject')}'</Typography>
            }
          />

          <Stack spacing={1} mt={4}>
            <Typography variant='h4' className={classes['title']}>
              {t('AddInfoTab.title_4')}
            </Typography>
            <Typography variant='caption' fontSize={14}>
              {data?.data.taxDebtInfo}
            </Typography>
          </Stack>

          <UITable
            sx={{
              mt: 4,
              '& .MuiTableHead-root': {
                backgroundColor: 'rgba(13, 29, 88, 0.04)',
                '& .MuiTableCell-root': {
                  color: 'text.secondary',
                },
              },
            }}
            data={data?.data.taxDept || ([{}] as AdditionalDataResponse['taxDept'])}
            columns={[
              {
                label: t('AddInfoTab.table_label_7'),
                key: 'taxType',
                sx: {
                  width: '40%',
                },
              },
              {
                label: t('AddInfoTab.table_label_10'),
                render: ({ debtAmount }) =>
                  triada(debtAmount, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_11'),
                render: ({ penalty }) =>
                  triada(penalty, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
              {
                label: t('AddInfoTab.table_label_12'),
                render: ({ prepayment }) =>
                  triada(prepayment, {
                    decimal: 2,
                  }),
                align: 'right',
                headerAlign: 'right',
              },
            ]}
          />

          <Typography variant='h4' className={classes['title']} mt={4}>
            {t('AddInfoTab.title_5')}
          </Typography>

          <Grid container sx={{ mt: 4 }} columnSpacing={4} rowSpacing={4}>
            <Grid item md={6} xs={12}>
              <Typography
                variant='h6'
                fontSize='1.7rem'
                className={classes['sub-title']}
                fontWeight={600}
              >
                {t('AddInfoTab.subtitle_5_1')}
              </Typography>

              <UITable
                sx={{
                  mt: 4,
                  '& .MuiTableHead-root': {
                    backgroundColor: 'rgba(13, 29, 88, 0.04)',
                    '& .MuiTableCell-root': {
                      color: 'text.secondary',
                    },
                  },
                }}
                data={data?.data.deskCheck ?? ([{}] as AdditionalDataResponse['deskCheck'])}
                columns={[
                  {
                    label: t('AddInfoTab.table_label_13'),
                    key: 'beginDate',
                  },
                  {
                    label: t('AddInfoTab.table_label_14'),
                    key: 'endDate',
                  },
                  {
                    label: t('AddInfoTab.table_label_15'),
                    key: 'checkResult',
                  },
                  {
                    label: t('AddInfoTab.table_label_16'),
                    render: ({ controlSum }) =>
                      triada(controlSum, {
                        decimal: 2,
                      }),
                    align: 'right',
                    headerAlign: 'right',
                  },
                ]}
                EmptyPlaceholder={
                  <Typography variant='caption'>{t('AddInfoTab.subtitle_5_2')}</Typography>
                }
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant='h6'
                fontSize='1.7rem'
                className={classes['sub-title']}
                fontWeight={600}
              >
                {t('AddInfoTab.subtitle_5_2')}
              </Typography>

              <UITable
                sx={{
                  mt: 4,
                }}
                data={data?.data.onsiteCheck || ([{}] as AdditionalDataResponse['onsiteCheck'])}
                columns={[
                  {
                    label: t('AddInfoTab.table_label_13'),
                    key: 'beginDate',
                  },
                  {
                    label: t('AddInfoTab.table_label_14'),
                    key: 'endDate',
                  },
                  {
                    label: t('AddInfoTab.table_label_15'),
                    key: 'checkResult',
                  },
                  {
                    label: t('AddInfoTab.table_label_16'),
                    render: ({ controlSum }) =>
                      triada(controlSum, {
                        decimal: 2,
                      }),
                    align: 'right',
                    headerAlign: 'right',
                  },
                  {
                    label: t('AddInfoTab.table_label_17'),
                    key: 'offenseName',
                  },
                ]}
                EmptyPlaceholder={
                  <EmptyDataPlaceholder isEmpty title={t('AddInfoTab.subtitle_5_3')} />
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ComponentWithLoader>
  );
};
